import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useBranding } from '../../context/BrandingContext';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export default function ForgotPassword() {
  const branding = useBranding();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const btnGradient = `linear-gradient(135deg, ${branding?.secondaryColor || '#e94560'}, ${branding?.primaryColor || '#1a1a2e'})`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || 'Request failed');
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8,
    padding: '12px 14px', color: C.t1, fontSize: 13, fontFamily: FONTS.mono, outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 400 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Reset Password</div>
        <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 28 }}>Enter your email and we'll send you a reset link</div>

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: C.redBg, border: `1px solid ${C.red}33`, marginBottom: 16, fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{error}</div>
        )}

        {sent ? (
          <div style={{ padding: 20, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: C.sky }}><Mail size={36} strokeWidth={1.5} /></div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 8 }}>Check your email</div>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20 }}>
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
            </div>
            <Link to="/login" style={{ fontSize: 11, color: C.sky, fontFamily: FONTS.mono, textDecoration: 'none' }}>{'\u2190'} Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Email Address</label>
              <input
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" type="email" autoFocus required
                style={inputStyle}
              />
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: loading ? 'wait' : 'pointer',
              background: loading ? C.t3 : btnGradient,
              color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans,
            }}>{loading ? 'Sending...' : 'Send Reset Link'}</button>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Link to="/login" style={{ fontSize: 11, color: C.sky, fontFamily: FONTS.mono, textDecoration: 'none' }}>{'\u2190'} Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
